import { ReactElement } from 'react';

import { Label, LABEL_TYPE } from '@/components/generic';
import { LeftChevronIcon } from '@/resources';

type SpinnerInputProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  icon?: ReactElement;
  unit?: string;
  label?: string;
  labelClasses?: string;
};

export const SpinnerInput = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  className = '',
  icon = undefined,
  unit = '',
  label,
  labelClasses = ''
}: SpinnerInputProps) => {
  const handleIncrement = () => {
    if (value + step <= max) {
      onChange(value + step);
    }
  };

  const handleDecrement = () => {
    if (value - step >= min) {
      onChange(value - step);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value.replace(unit, '').trim();
    if (inputValue === '') {
      inputValue = '0';
    }
    const numericValue = parseFloat(inputValue);
    if (!isNaN(numericValue)) {
      const clampedValue = Math.max(min, Math.min(max, numericValue));
      onChange(clampedValue);
    }
  };

  return (
    <div className={`${className}`}>
      {label && (
        <Label
          text={label}
          type={LABEL_TYPE.PARAGRAPH2}
          className={`mb-2.5 !text-black ${labelClasses}`}
        />
      )}
      <div className='flex h-10 items-center justify-between rounded-xl border border-secondary-blue-gray px-3'>
        <div>{icon}</div>
        <input
          type='text'
          value={value + unit}
          onChange={handleInputChange}
          className='w-12 text-center outline-none'
        />
        <div className='flex cursor-pointer flex-col items-center justify-center'>
          <LeftChevronIcon
            className={`mr-0.5 size-3 rotate-90 text-secondary-blue-gray hover:text-secondary-dark-steel-blue ${
              value >= max && 'cursor-not-allowed'
            }`}
            onClick={handleIncrement}
          />
          <LeftChevronIcon
            className={`size-3 -rotate-90 text-secondary-blue-gray hover:text-secondary-dark-steel-blue ${
              value <= min && 'cursor-not-allowed'
            }`}
            onClick={handleDecrement}
          />
        </div>
      </div>
    </div>
  );
};

export default SpinnerInput;
